import { styles } from "@repo/helpers";
import { Carousel } from "@repo/ui";
import { useCallback, useEffect, useRef, useState } from "react";

import { useDynamicSectionContext } from "../contexts/dynamicSectionContext";
import DynamicSection from "../dynamicSection";
import { sectionType } from "../models/enums";
import style from "./style.module.scss";
import { FlattenedSection, MobileProps } from "./types";

export default function MobileDynamicComponentContainer(props: MobileProps) {
	const { activeIndex } = props;
	const { allSections } = useDynamicSectionContext();

	const wrapperRef = useRef<HTMLSelectElement>();
	useEffect(() => {
		wrapperRef.current.children[0].scroll(0, 0);
	}, [activeIndex]);

	const [mountedSections, setMountedSections] = useState(new Set<string>());

	useEffect(() => {
		if (!allSections?.[activeIndex]) return;
		const newMounted = new Set(mountedSections);
		const currentActive = allSections[activeIndex].linkName;
		if (!newMounted.has(currentActive)) {
			newMounted.add(currentActive);
			setMountedSections(newMounted);
		}
	}, [activeIndex]);

	const prev = useCallback(() => props.indexChanged(activeIndex - 1), [props.indexChanged, activeIndex]);
	const next = useCallback(() => props.indexChanged(activeIndex + 1), [props.indexChanged, activeIndex]);

	if (!allSections) return null;

	// Submit only button when no documents available
	if (allSections.length === 0) {
		return (
			<section id="dynamicSectionWrapper" className={style.container} ref={wrapperRef}>
				<div className={style.buttonOnlyContainer}>
					<div className={style.buttonOnly}>{props.children}</div>
				</div>
			</section>
		);
	}

	//turning off swipe for the moment, as doesn't listen to our validation rules
	return (
		<section id="dynamicSectionWrapper" className={style.container} ref={wrapperRef}>
			<Carousel
				index={activeIndex}
				next={next}
				prev={prev}
				autoPlay={false}
				cycleNavigation={false}
				swipe={false}
				animation="slide"
				navButtonsAlwaysInvisible
				className={style.carousel}
				indicators={false}
			>
				{allSections.map(renderSection)}
			</Carousel>
		</section>
	);

	function renderSection(section: FlattenedSection, i: number) {
		const { parentPropertyName, index, linkName } = section;
		const mappedProps = { ...props, sections: null, activeIndex: null };
		const isActive = i === activeIndex;
		const hideChildSections =
			section.section.type !== sectionType.summary && section.section.type !== sectionType.iteratedEditable;
		if (!isActive && !mountedSections.has(linkName))
			return <div className={style.sectionContainer} key={linkName}></div>;
		return (
			<div className={styles(style.sectionContainer, !isActive && style.hidden)} key={linkName}>
				<DynamicSection
					{...mappedProps}
					section={section}
					sectionOpen={isActive}
					hideChildSections={hideChildSections}
					parentPropertyName={parentPropertyName}
					index={index}
				/>
				{renderButtons(i)}
			</div>
		);
	}

	function renderButtons(index: number) {
		//Only display button for last section
		if (index < allSections.length - 1) return null;
		return props.children;
	}
}
